import { call, put, select, takeLatest } from 'redux-saga/effects';
import { createAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import {
  fetchQueryLogs,
  fetchBrands,
  fetchCategories,
  fetchCompetitorsQueryLogs,
} from '@/api/products';
import {
  setQueryLogs,
  setBrands,
  setCategories,
  setLoading,
} from '../slices/dashboardSlice';

// Action creators
export const fetchDashboardData = createAction('dashboard/fetchDashboardData');
export const fetchCompetitorsDashboardData = createAction('dashboard/fetchCompetitorsDashboardData');
export const fetchMoreBrands = createAction<{ page: number }>('dashboard/fetchMoreBrands');
export const fetchMoreCategories = createAction<{ page: number }>('dashboard/fetchMoreCategories');

function* fetchDashboardDataSaga() {
  try {
    yield put(setLoading(true));
    
    const state: RootState = yield select();
    const {
      selectedBrand,
      selectedCategory,
      searchTerm,
      currentPage,
    } = state.dashboard;

    const params = {
      brand: selectedBrand || undefined,
      category: selectedCategory || undefined,
      search: searchTerm || undefined,
      page: currentPage,
    };

    const queryLogsResponse = yield call(fetchQueryLogs, params);
    yield put(setQueryLogs(queryLogsResponse));
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchCompetitorsDashboardDataSaga() {
  try {
    yield put(setLoading(true));
    
    const state: RootState = yield select();
    const {
      selectedBrand,
      selectedCategory,
      searchTerm,
      currentPage,
    } = state.dashboard;

    const { selectedCompetitors } = state.competitors;
    const provider_ids = selectedCompetitors.map(comp => comp.id).join(',');

    const params = {
      brand: selectedBrand || undefined,
      category: selectedCategory || undefined,
      search: searchTerm || undefined,
      page: currentPage,
      provider_ids: provider_ids || undefined,
    };

    const queryLogsResponse = yield call(fetchCompetitorsQueryLogs, params);
    yield put(setQueryLogs(queryLogsResponse));
  } catch (error) {
    console.error('Error fetching competitors dashboard data:', error);
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchMoreBrandsSaga(action: ReturnType<typeof fetchMoreBrands>) {
  try {
    const response = yield call(fetchBrands, {
      page: action.payload.page,
      page_size: 100,
    });
    yield put(setBrands(response.results));
  } catch (error) {
    console.error('Error fetching more brands:', error);
  }
}

function* fetchMoreCategoriesSaga(action: ReturnType<typeof fetchMoreCategories>) {
  try {
    const response = yield call(fetchCategories, {
      page: action.payload.page,
      page_size: 100,
    });
    yield put(setCategories(response.results));
  } catch (error) {
    console.error('Error fetching more categories:', error);
  }
}

export function* dashboardSaga() {
  yield takeLatest(fetchDashboardData.type, fetchDashboardDataSaga);
  yield takeLatest(fetchCompetitorsDashboardData.type, fetchCompetitorsDashboardDataSaga);
  yield takeLatest(fetchMoreBrands.type, fetchMoreBrandsSaga);
  yield takeLatest(fetchMoreCategories.type, fetchMoreCategoriesSaga);
}
